import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { IRide, IRidePosition, TRideActiveLayer } from '../types';
import {
  createCombinedEventIconMarkers,
  createEventPositionMarkerTooltips,
  IRideMapCombinedEventMarkers,
  IRideMapEventPositionMarkerTooltips,
  updateEventIconMarkersWithNewPosition,
} from '../RidePlayerEventMarkerService';
import { IS_EVENT_LAYER } from '../constants';
import { activeRideNumberAtom, selectedRidesAtom } from '../RidePlayerState';
import { hideAllTooltips } from '../RidePlayerMarkerService';

interface IUseEventMarker {
  eventMarkers: IRideMapCombinedEventMarkers;
  eventPositionMarkerTooltips: IRideMapEventPositionMarkerTooltips;
  showAllEventIconMarkers: () => void;
  toggleEventPositionMarkerTooltips(toggleValue: boolean): void;
  hideAllEventIconMarkers: () => void;
  setEventIconMarkersOpacity: (rideNumber: number, opacity: number) => void;
  setEventIconMarkerOpacity: (
    rideNumber: number,
    position: google.maps.LatLng,
    opacity: number
  ) => void;
  setAllEventIconMarkersOpacity: (opacity: number, exceptRideNumber?: number) => void;
}

/**
 * useEventMarker custom hook
 * @param {google.maps.Map} map
 * @returns {IUseEcoDriveMarker}
 */
const useEventMarker = (
  map: google.maps.Map,
  rides: IRide[],
  activeLayer: TRideActiveLayer,
  zoomLevel: number,
  actualRidePosition?: IRidePosition | null
): IUseEventMarker => {
  const intl = useIntl();
  const eventMarkers = React.useRef<IRideMapCombinedEventMarkers>({ 1: {} });
  const eventPositionMarkerTooltips = React.useRef<IRideMapEventPositionMarkerTooltips>({
    1: [],
  });
  const selectedRides = useRecoilValue<number[]>(selectedRidesAtom);
  const activeRide = useRecoilValue<number>(activeRideNumberAtom);

  /**
   * Set all event icon markers opacity for specified ride
   * @param {number} rideNumber
   * @param {number} opacity
   * @returns {void}
   */
  const setEventIconMarkersOpacity = (rideNumber: number, opacity: number): void => {
    Object.keys(eventMarkers.current[Number(rideNumber)]).forEach((positionIndex: string) => {
      const markerData = eventMarkers.current[Number(rideNumber)][Number(positionIndex)];
      if (markerData) {
        markerData.marker.setOpacity(opacity);
      }
    });
  };

  /**
   * Set all event icon markers opacity for all rides
   * @param {number} rideNumber
   * @param {number} opacity
   * @returns {void}
   */
  const setAllEventIconMarkersOpacity = (opacity: number, exceptRideNumber?: number): void => {
    if (eventMarkers.current) {
      Object.keys(eventMarkers.current).forEach((rideNumber: string) => {
        if (exceptRideNumber && exceptRideNumber === Number(rideNumber)) {
          return;
        }
        Object.keys(eventMarkers.current[rideNumber]).forEach((positionIndex: string) => {
          const marker = eventMarkers.current[rideNumber][positionIndex].marker;
          marker.setOpacity(opacity);
        });
      });
    }
  };

  /**
   * Set event icon marker opacity by position and ride
   * @param {number} rideNumber
   * @param {google.maps.LatLng} position
   * @param {number} opacity
   * @returns {void}
   */
  const setEventIconMarkerOpacity = (
    rideNumber: number,
    position: google.maps.LatLng,
    opacity: number
  ): void => {
    Object.keys(eventMarkers.current[Number(rideNumber)]).forEach((positionIndex: string) => {
      const markerData = eventMarkers.current[Number(rideNumber)][Number(positionIndex)];
      if (markerData && markerData.marker) {
        const pos = markerData.marker.getPosition();
        if (pos && pos.toString() === position.toString()) {
          markerData.marker.setOpacity(opacity);
        }
      }
    });
  };

  /**
   * Show all event icon markers
   * @returns void
   */
  const showAllEventIconMarkers = (): void => {
    Object.keys(eventMarkers.current).forEach((rideNumber: string) => {
      Object.keys(eventMarkers.current[Number(rideNumber)]).forEach((positionIndex: string) => {
        const markerData = eventMarkers.current[Number(rideNumber)][Number(positionIndex)];
        if (
          markerData &&
          !markerData.marker.getMap() &&
          selectedRides.includes(Number(rideNumber))
        ) {
          markerData.marker.setMap(map);
        }
      });
    });
  };

  /**
   * Hide all event icon markers
   * @returns void
   */
  const hideAllEventIconMarkers = (): void => {
    Object.keys(eventMarkers.current).forEach((rideNumber: string) => {
      Object.keys(eventMarkers.current[Number(rideNumber)]).forEach((positionIndex: string) => {
        const markerData = eventMarkers.current[Number(rideNumber)][Number(positionIndex)];
        if (markerData && markerData.marker.getMap()) {
          markerData.marker.setMap(null);
        }
      });
    });
  };

  /**
   * Show/hide all event position marker tooltips
   * @param {boolean} toggleValue
   * @returns void
   */
  const toggleEventPositionMarkerTooltips = (toggleValue: boolean): void => {
    Object.keys(eventPositionMarkerTooltips.current).forEach((rideNumber: string) => {
      eventPositionMarkerTooltips.current[Number(rideNumber)].forEach(
        (tooltip: google.maps.OverlayView | null) => {
          if (tooltip && toggleValue && !tooltip.getMap()) {
            tooltip.setMap(map);
          }
          if (tooltip && !toggleValue && tooltip.getMap()) {
            tooltip.setMap(null);
          }
        }
      );
    });
  };

  /**
   * Toggle ecoDrive tooltip
   * @param {number} rideNumber
   * @param {number} positionIndex
   * @returns {void}
   */
  const handleToggleEventPositionTooltip = (rideNumber: number, positionIndex: number): void => {
    const eventPositionTooltip = eventPositionMarkerTooltips.current[rideNumber][positionIndex];
    hideAllTooltips(eventPositionMarkerTooltips, positionIndex);
    if (eventPositionTooltip) {
      eventPositionTooltip.setMap(eventPositionTooltip.getMap() ? null : map);
    }
  };

  // Handle show/hide event markers by selected rides
  React.useEffect(() => {
    Object.keys(eventMarkers.current).forEach((rideNumber: string) => {
      Object.keys(eventMarkers.current[Number(rideNumber)]).forEach((positionIndex: string) => {
        const marker = eventMarkers.current[Number(rideNumber)][positionIndex].marker;
        if (!selectedRides.includes(Number(rideNumber)) && marker.getMap()) {
          marker.setMap(null);
        }
        if (
          selectedRides.includes(Number(rideNumber)) &&
          !marker.getMap() &&
          activeLayer === IS_EVENT_LAYER
        ) {
          marker.setMap(map);
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLayer, map, selectedRides]);

  React.useEffect(() => {
    eventMarkers.current = createCombinedEventIconMarkers(
      activeLayer === IS_EVENT_LAYER ? map : undefined,
      rides,
      handleToggleEventPositionTooltip,
      activeRide
    );
    eventPositionMarkerTooltips.current = createEventPositionMarkerTooltips(rides, intl);

    // Unset event markers and tooltips from map
    return () => {
      hideAllEventIconMarkers();
      eventMarkers.current = { 1: [] };
      toggleEventPositionMarkerTooltips(false);
      eventPositionMarkerTooltips.current = { 1: [] };
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If actual position is present, update event marker and tooltip
  React.useEffect(() => {
    if (actualRidePosition && activeRide) {
      const positionIndex = rides[activeRide - 1].positions.length - 1;
      const ride = rides[activeRide - 1];
      updateEventIconMarkersWithNewPosition(
        activeLayer === IS_EVENT_LAYER ? map : undefined,
        eventMarkers.current,
        ride,
        activeRide,
        actualRidePosition,
        positionIndex,
        handleToggleEventPositionTooltip
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualRidePosition, activeRide, map]);

  const data: IUseEventMarker = {
    eventMarkers: eventMarkers.current,
    eventPositionMarkerTooltips: eventPositionMarkerTooltips.current,
    showAllEventIconMarkers,
    toggleEventPositionMarkerTooltips,
    hideAllEventIconMarkers,
    setEventIconMarkerOpacity,
    setEventIconMarkersOpacity,
    setAllEventIconMarkersOpacity,
  };

  return data;
};

export default useEventMarker;
